import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { finalize, forkJoin, switchMap } from 'rxjs';
import { SessionStorageService } from '../services/session-storage.service';
import { app_permissions } from '../security/app-permissions';
import { PermissionService } from '../security/permission.service';
import {
    ReportAggregate,
    ReportColumn,
    ReportDefinition,
    ReportFilter,
    ReportTemplate,
    ReportTemplateBindingCatalog,
    ReportTemplateVersion,
    ReportPreviewResponse,
    ReportSort,
    ReportingDataGroup,
    ReportingDataset,
    ReportingField,
    SqlReportPreviewRequest,
    SqlReportPreviewResult,
    SqlReportExportRequest,
    SqlReportQuery,
    SqlReportSource,
    SqlReportValidation,
    TemplateBindingField,
    TemplatePreviewResponse
} from './reporting.models';
import { ReportingService } from './reporting.service';
import { PendingActionTracker } from '../shared/state/pending-action-tracker';

@Component({
    selector: 'app-reports-workspace',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputNumberModule,
        InputTextModule,
        ProgressBarModule,
        SelectModule,
        TableModule,
        TagModule,
        TextareaModule
    ],
    templateUrl: './reports-workspace.component.html',
    styleUrls: ['./reports-workspace.component.css']
})
export class ReportsWorkspaceComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('templateEditorHost') templateEditorHost?: ElementRef<HTMLDivElement>;
    @ViewChild('sqlEditorHost') sqlEditorHost?: ElementRef<HTMLDivElement>;

    datasets: ReportingDataset[] = [];
    savedReports: ReportDefinition[] = [];
    selectedDataset?: ReportingDataset;
    definition: ReportDefinition;
    preview?: ReportPreviewResponse;
    loading = false;
    saving = false;
    readonly reportActions = new PendingActionTracker<string>();
    errorMessage = '';
    fieldSearch = '';
    selectedFieldKeys: string[] = [];
    expandedFieldPaths = new Set<string>();
    canViewShipmentPricing = false;
    activeWorkspace: 'builder' | 'templates' | 'sql' = 'builder';
    templates: ReportTemplate[] = [];
    selectedTemplate?: ReportTemplate;
    templateVersions: ReportTemplateVersion[] = [];
    bindingCatalog?: ReportTemplateBindingCatalog;
    templatePreview?: TemplatePreviewResponse;
    templateLoading = false;
    templateSaving = false;
    templateErrorMessage = '';
    templateSearch = '';
    templateTypeFilter = 'ALL';
    bindingSearch = '';
    selectedReportType = 'DELIVERY_NOTE';
    templateName = 'Delivery Note';
    templateDescription = '';
    templateHtml = this.defaultTemplateHtml();
    sampleContextText = '{}';
    templatePreviewMode: 'HTML' | 'PDF' = 'HTML';
    templateEditorFallback = false;
    canViewTemplates = false;
    canCreateTemplates = false;
    canEditTemplates = false;
    canPublishTemplates = false;
    canPreviewTemplates = false;
    canViewSql = false;
    canEditSql = false;
    canExecuteSql = false;
    canExportSql = false;
    canPublishSql = false;
    sqlQueries: SqlReportQuery[] = [];
    sqlSources: SqlReportSource[] = [];
    sqlSourceSearch = '';
    sqlSourceCategory = 'ALL';
    sqlModelSearch = '';
    selectedSqlQuery?: SqlReportQuery;
    selectedSqlSource?: SqlReportSource;
    sqlQueryName = 'Untitled SQL Data Model';
    sqlDescription = '';
    sqlText = this.defaultSqlText();
    sqlParametersText = '{\n  "fromDate": "2026-01-01"\n}';
    sqlMaxRows = 100;
    sqlValidation?: SqlReportValidation;
    sqlPreview?: SqlReportPreviewResult;
    sqlLoading = false;
    sqlSaving = false;
    sqlExportingFormat = '';
    sqlErrorMessage = '';
    sqlEditorFallback = false;
    private templateEditor?: any;
    private templateEditorInitializing = false;
    private sqlEditor?: any;
    private sqlEditorInitializing = false;
    private static monacoLoaderPromise?: Promise<any>;
    newFilter: ReportFilter = { fieldKey: '', operator: 'CONTAINS', value: '' };
    newSort: ReportSort = { fieldKey: '', direction: 'ASC' };
    newAggregateFieldKey = '';
    newAggregateFunction = 'SUM';
    activeRuleTab: 'filters' | 'sorts' | 'totals' = 'filters';
    dataModelViewMode: 'diagram' | 'structure' | 'code' = 'diagram';
    activeGroupFilter = 'ALL';
    catalogSearch = '';
    rightPaneCollapsed = false;
    leftPaneCollapsed = false;
    collapsedDataGroups = new Set<string>();
    collapsedTreeSections = new Set<string>();
    collapsedDatasetModules = new Set<string>();
    draggedField?: ReportingField;
    dragOverGroupKey = '';
    openActionFieldKey = '';
    readonly aggregateFunctions = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT'];
    readonly formats = ['pdf', 'xlsx', 'csv', 'docx', 'pptx'];
    readonly orientations = ['LANDSCAPE', 'PORTRAIT'];
    readonly pageSizes = ['A4', 'Letter'];
    readonly filterOperators = [
        { label: 'Contains', value: 'CONTAINS' },
        { label: 'Equals', value: 'EQ' },
        { label: 'Not equal', value: 'NE' },
        { label: 'Greater than', value: 'GT' },
        { label: 'Greater/equal', value: 'GTE' },
        { label: 'Less than', value: 'LT' },
        { label: 'Less/equal', value: 'LTE' }
    ];
    readonly sortDirections = [
        { label: 'Ascending', value: 'ASC' },
        { label: 'Descending', value: 'DESC' }
    ];
    readonly reportTypeOptions = [
        'INVOICE',
        'SINGLE_INVOICE_B2B',
        'SINGLE_INVOICE_B2C',
        'CONSOLIDATED_INVOICE',
        'WAYBILL',
        'DELIVERY_NOTE',
        'BILL',
        'CLEARANCE',
        'CREDIT_NOTE',
        'DEBIT_NOTE',
        'RECEIPT',
        'VOUCHER',
        'CONTRACT_DOCUMENT',
        'CRM_QUOTATION',
        'PICK_LIST',
        'LOADGATE_SHIPMENT_AGREEMENT'
    ];

    constructor(
        private readonly reportingService: ReportingService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly permissionService: PermissionService
    ) {
        this.definition = this.emptyDefinition('shipments');
    }

    ngOnInit(): void {
        this.canViewShipmentPricing = this.permissionService.hasPermission(app_permissions.shipments.pricing.view);
        this.canViewTemplates = this.permissionService.hasPermission(app_permissions.reporting.templates.view);
        this.canCreateTemplates = this.permissionService.hasPermission(app_permissions.reporting.templates.create);
        this.canEditTemplates = this.permissionService.hasPermission(app_permissions.reporting.templates.edit);
        this.canPublishTemplates = this.permissionService.hasPermission(app_permissions.reporting.templates.publish);
        this.canPreviewTemplates = this.permissionService.hasPermission(app_permissions.reporting.templates.preview);
        this.canViewSql = this.permissionService.hasPermission(app_permissions.reporting.sql.view);
        this.canEditSql = this.permissionService.hasPermission(app_permissions.reporting.sql.edit);
        this.canExecuteSql = this.permissionService.hasPermission(app_permissions.reporting.sql.execute);
        this.canExportSql = this.permissionService.hasPermission(app_permissions.reporting.sql.export);
        this.canPublishSql = this.permissionService.hasPermission(app_permissions.reporting.sql.publish);
        this.loading = true;
        this.reportingService.getDatasets()
            .pipe(finalize(() => this.loading = false))
            .subscribe({
                next: datasets => {
                    this.datasets = datasets;
                    const firstDataset = datasets[0];
                    if (firstDataset) {
                        this.applyDataset(firstDataset);
                        const activeModule = firstDataset.module || 'General';
                        const initialCollapsed = new Set<string>();
                        for (const d of datasets) {
                            const m = d.module || 'General';
                            if (m !== activeModule) {
                                initialCollapsed.add(m);
                            }
                        }
                        this.collapsedDatasetModules = initialCollapsed;
                    }
                    this.loadReports();
                },
                error: () => this.errorMessage = 'Could not load reporting datasets.'
            });
    }

    ngAfterViewInit(): void {
        if (this.activeWorkspace === 'templates') {
            this.initTemplateEditor();
        } else if (this.activeWorkspace === 'sql') {
            this.initSqlEditor();
        }
    }

    ngOnDestroy(): void {
        this.templateEditor?.dispose?.();
        this.sqlEditor?.dispose?.();
    }

    switchWorkspace(workspace: 'builder' | 'templates' | 'sql'): void {
        if (workspace === 'templates' && !this.canViewTemplates) {
            return;
        }
        if (workspace === 'sql' && !this.canViewSql) {
            return;
        }
        this.activeWorkspace = workspace;
        if (workspace === 'templates') {
            this.loadTemplates();
            setTimeout(() => this.initTemplateEditor());
        } else if (workspace === 'sql') {
            this.loadSqlWorkspace();
            setTimeout(() => this.initSqlEditor());
        }
    }

    loadReports(): void {
        const companyId = this.companyId();
        if (!companyId) {
            this.savedReports = [];
            return;
        }
        this.reportingService.getReports(companyId)
            .subscribe({
                next: reports => this.savedReports = reports,
                error: () => this.savedReports = []
            });
    }

    loadSqlWorkspace(): void {
        const companyId = this.companyId();
        if (!companyId || !this.canViewSql) {
            this.sqlQueries = [];
            this.sqlSources = [];
            this.sqlSourceSearch = '';
            return;
        }
        this.sqlLoading = true;
        this.sqlErrorMessage = '';
        forkJoin({
            queries: this.reportingService.getSqlQueries(companyId),
            sources: this.reportingService.getSqlSources(companyId)
        })
            .pipe(finalize(() => this.sqlLoading = false))
            .subscribe({
                next: response => {
                    this.sqlQueries = response.queries;
                    this.sqlSources = [...response.sources].sort((left, right) =>
                        `${left.category || 'Other'}:${left.label}`.localeCompare(`${right.category || 'Other'}:${right.label}`));
                    if ((!this.selectedSqlSource
                        || !this.sqlSources.some(source => source.sourceName === this.selectedSqlSource?.sourceName))
                        && this.sqlSources.length) {
                        this.selectedSqlSource = this.sqlSources[0];
                    }
                },
                error: error => this.sqlErrorMessage = this.apiError(error, 'Could not load the SQL reporting workspace.')
            });
    }

    filteredSqlQueries(): SqlReportQuery[] {
        const search = this.sqlModelSearch.trim().toLowerCase();
        if (!search) {
            return this.sqlQueries;
        }
        return this.sqlQueries.filter(q =>
            [q.queryName, q.sourceName, q.description]
                .some(val => String(val || '').toLowerCase().includes(search))
        );
    }

    sqlSourceCategories(): string[] {
        const categories = new Set(this.sqlSources.map(s => s.category || 'General'));
        return Array.from(categories).sort();
    }

    filteredSqlSources(): SqlReportSource[] {
        const search = this.sqlSourceSearch.trim().toLowerCase();
        return this.sqlSources.filter(source => {
            const matchesCat = this.sqlSourceCategory === 'ALL' || (source.category || 'General') === this.sqlSourceCategory;
            if (!matchesCat) return false;
            if (!search) return true;
            return [source.sourceName, source.label, source.category,
                source.sourceType, source.description, source.requiredPermission]
                .some(value => String(value || '').toLowerCase().includes(search));
        });
    }

    sqlColumnTypeBadge(dataType: string): string {
        const type = (dataType || '').toUpperCase();
        if (type.includes('NUM') || type.includes('INT') || type.includes('DECIMAL') || type.includes('FLOAT') || type.includes('DOUBLE')) return '999';
        if (type.includes('DATE') || type.includes('TIME')) return 'DATE';
        if (type.includes('BOOL')) return 'BOOL';
        if (type.includes('JSON')) return 'JSON';
        return 'ABC';
    }

    sqlColumnTypeClass(dataType: string): string {
        const type = (dataType || '').toUpperCase();
        if (type.includes('NUM') || type.includes('INT') || type.includes('DECIMAL') || type.includes('FLOAT') || type.includes('DOUBLE')) return 'type-number';
        if (type.includes('DATE') || type.includes('TIME')) return 'type-date';
        if (type.includes('BOOL')) return 'type-enum';
        if (type.includes('JSON')) return 'type-currency';
        return 'type-text';
    }

    newSqlQuery(): void {
        this.selectedSqlQuery = undefined;
        this.sqlQueryName = 'Untitled SQL Data Model';
        this.sqlDescription = '';
        this.sqlParametersText = '{\n  "fromDate": "2026-01-01"\n}';
        this.sqlValidation = undefined;
        this.sqlPreview = undefined;
        this.sqlErrorMessage = '';
        this.setSqlEditorValue(this.defaultSqlText());
    }

    openSqlQuery(query: SqlReportQuery): void {
        this.selectedSqlQuery = query;
        this.sqlQueryName = query.queryName;
        this.sqlDescription = query.description || '';
        this.sqlValidation = undefined;
        this.sqlPreview = undefined;
        this.sqlErrorMessage = '';
        this.setSqlEditorValue(query.sqlText);
    }

    saveSqlQuery(): void {
        if (!this.canEditSelectedSql()) {
            return;
        }
        const companyId = this.companyId();
        if (!companyId || !this.sqlQueryName.trim()) {
            this.sqlErrorMessage = 'Company context and query name are required.';
            return;
        }
        this.sqlSaving = true;
        this.sqlErrorMessage = '';
        this.reportingService.saveSqlQuery({
            companyId,
            queryName: this.sqlQueryName.trim(),
            description: this.sqlDescription,
            sqlText: this.sqlText
        }, this.selectedSqlQuery?.queryId)
            .pipe(finalize(() => this.sqlSaving = false))
            .subscribe({
                next: query => {
                    this.selectedSqlQuery = query;
                    this.sqlQueryName = query.queryName;
                    this.sqlDescription = query.description || '';
                    this.setSqlEditorValue(query.sqlText);
                    this.loadSqlWorkspace();
                },
                error: error => this.sqlErrorMessage = this.apiError(error, 'Could not save the SQL data model.')
            });
    }

    publishSqlQuery(): void {
        if (!this.canPublishSql || !this.selectedSqlQuery?.queryId || !this.selectedSqlQuery.editable) {
            return;
        }
        this.sqlSaving = true;
        this.sqlErrorMessage = '';
        this.reportingService.publishSqlQuery(this.selectedSqlQuery.queryId)
            .pipe(finalize(() => this.sqlSaving = false))
            .subscribe({
                next: query => {
                    this.selectedSqlQuery = query;
                    this.loadSqlWorkspace();
                },
                error: error => this.sqlErrorMessage = this.apiError(error, 'Could not publish the SQL data model.')
            });
    }

    unpublishSqlQuery(): void {
        if (!this.canPublishSql || !this.selectedSqlQuery?.queryId || !this.selectedSqlQuery.editable) {
            return;
        }
        this.sqlSaving = true;
        this.sqlErrorMessage = '';
        this.reportingService.unpublishSqlQuery(this.selectedSqlQuery.queryId)
            .pipe(finalize(() => this.sqlSaving = false))
            .subscribe({
                next: query => {
                    this.selectedSqlQuery = query;
                    this.loadSqlWorkspace();
                },
                error: error => this.sqlErrorMessage = this.apiError(error, 'Could not unpublish the SQL data model.')
            });
    }

    validateSqlQuery(): void {
        const request = this.sqlPreviewRequest(false);
        if (!request) {
            return;
        }
        this.sqlLoading = true;
        this.sqlErrorMessage = '';
        this.reportingService.validateSqlQuery(request)
            .pipe(finalize(() => this.sqlLoading = false))
            .subscribe({
                next: validation => this.sqlValidation = validation,
                error: error => this.sqlErrorMessage = this.apiError(error, 'Could not validate the SQL statement.')
            });
    }

    previewSqlQuery(): void {
        if (!this.canExecuteSql) {
            return;
        }
        const request = this.sqlPreviewRequest(true);
        if (!request) {
            return;
        }
        this.sqlLoading = true;
        this.sqlErrorMessage = '';
        this.sqlPreview = undefined;
        this.reportingService.previewSqlQuery(request)
            .pipe(finalize(() => this.sqlLoading = false))
            .subscribe({
                next: preview => {
                    this.sqlPreview = preview;
                    this.sqlValidation = {
                        valid: true,
                        sources: preview.sources,
                        parameterNames: Object.keys(request.parameters),
                        diagnostics: []
                    };
                },
                error: error => this.sqlErrorMessage = this.apiError(error, 'The secure SQL preview could not be run.')
            });
    }

    exportSqlQuery(format: string): void {
        if (!this.canExportSql) {
            return;
        }
        const previewRequest = this.sqlPreviewRequest(true);
        if (!previewRequest) {
            return;
        }
        const request: SqlReportExportRequest = {
            companyId: previewRequest.companyId,
            queryName: this.sqlQueryName.trim() || 'SQL Data Model',
            sqlText: previewRequest.sqlText,
            parameters: previewRequest.parameters,
            format
        };
        this.sqlExportingFormat = format;
        this.sqlErrorMessage = '';
        this.reportingService.exportSqlQuery(request)
            .pipe(finalize(() => this.sqlExportingFormat = ''))
            .subscribe({
                next: run => {
                    if (run.status === 'COMPLETED' && run.base64Content) {
                        this.download(run.base64Content, run.fileName, run.contentType);
                    } else {
                        this.sqlErrorMessage = run.errorMessage || 'The SQL export failed.';
                    }
                },
                error: error => this.sqlErrorMessage = this.apiError(error, 'The SQL export could not be created.')
            });
    }

    selectSqlSource(source: SqlReportSource): void {
        this.selectedSqlSource = source;
    }

    insertSqlSource(source: SqlReportSource): void {
        const snippet = source.sourceName;
        if (this.sqlEditor) {
            const selection = this.sqlEditor.getSelection();
            this.sqlEditor.executeEdits('source-insert', [{ range: selection, text: snippet, forceMoveMarkers: true }]);
            this.sqlText = this.sqlEditor.getValue();
            this.sqlEditor.focus();
            return;
        }
        this.sqlText += snippet;
    }

    canEditSelectedSql(): boolean {
        return this.canEditSql && (!this.selectedSqlQuery || this.selectedSqlQuery.editable === true);
    }

    sqlCell(row: Record<string, unknown>, columnName: string): string {
        const value = row[columnName];
        if (value === null || value === undefined || value === '') {
            return '';
        }
        const col = this.sqlPreview?.columns?.find(c => c.name === columnName);
        const colType = (col?.dataType || '').toUpperCase();
        const isDateCol = colType.includes('TIMESTAMP') || colType.includes('DATE') || colType.includes('TIME')
                || this.isDateField(columnName);

        if (isDateCol) {
            const formatted = this.formatDateValue(value);
            if (formatted) {
                return formatted;
            }
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T|\s)\d{2}:\d{2}/.test(value)) {
            const formatted = this.formatDateValue(value);
            if (formatted) {
                return formatted;
            }
        }
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }

    sqlDiagnostics(): string {
        return (this.sqlValidation?.diagnostics || []).join('\n');
    }

    onDatasetChange(datasetKey: string): void {
        const dataset = this.datasets.find(item => item.datasetKey === datasetKey);
        if (dataset) {
            this.applyDataset(dataset);
        }
    }

    openReport(report: ReportDefinition): void {
        this.definition = this.cloneReport(report);
        this.selectedDataset = this.datasets.find(dataset => dataset.datasetKey === report.datasetKey);
        this.removeRestrictedPricingSelections();
        this.previewReport();
    }

    newReport(): void {
        const dataset = this.selectedDataset ?? this.datasets[0];
        if (!dataset) {
            return;
        }
        this.applyDataset(dataset);
        this.preview = undefined;
    }

    toggleColumn(field: ReportingField, checked: boolean): void {
        if (!this.isSelectableField(field)) {
            return;
        }
        if (checked && !this.selectedFieldKeys.includes(field.fieldKey)) {
            this.selectedFieldKeys = [...this.selectedFieldKeys, field.fieldKey];
        }
        if (!checked) {
            this.selectedFieldKeys = this.selectedFieldKeys.filter(key => key !== field.fieldKey);
        }
        this.definition.columns = this.selectedFieldKeys
            .map(key => this.selectedDataset?.fields.find(field => field.fieldKey === key))
            .filter((field): field is ReportingField => !!field)
            .map(field => this.columnFromField(field));
    }

    removeColumn(fieldKey: string): void {
        const field = this.selectedDataset?.fields.find(item => item.fieldKey === fieldKey);
        if (field) {
            this.toggleColumn(field, false);
        }
    }

    moveColumn(fieldKey: string, direction: -1 | 1): void {
        const currentIndex = this.selectedFieldKeys.indexOf(fieldKey);
        const nextIndex = currentIndex + direction;
        if (currentIndex < 0 || nextIndex < 0 || nextIndex >= this.selectedFieldKeys.length) {
            return;
        }
        const nextKeys = [...this.selectedFieldKeys];
        [nextKeys[currentIndex], nextKeys[nextIndex]] = [nextKeys[nextIndex], nextKeys[currentIndex]];
        this.selectedFieldKeys = nextKeys;
        this.definition.columns = this.selectedFieldKeys
            .map(key => this.selectedDataset?.fields.find(field => field.fieldKey === key))
            .filter((field): field is ReportingField => !!field)
            .map(field => this.columnFromField(field));
    }

    isColumnSelected(fieldKey: string): boolean {
        return this.selectedFieldKeys.includes(fieldKey);
    }

    addFilter(): void {
        const field = this.selectedFilterField();
        const value = this.newFilter.value;
        const valueType = this.filterValueType();
        if (!field || value === null || value === '' || !this.availableFilterOperators().some(option => option.value === this.newFilter.operator)
            || value instanceof Date && isNaN(value.getTime())
            || valueType === 'number' && (typeof value !== 'number' || !Number.isFinite(value))
            || valueType === 'date' && !(value instanceof Date)) {
            return;
        }
        this.definition.filters = [...this.definition.filters, {
            ...this.newFilter,
            value: value instanceof Date ? this.dateFilterValue(value) : value
        }];
        this.newFilter = { fieldKey: '', operator: 'CONTAINS', value: '' };
    }

    selectedFilterField(): ReportingField | undefined {
        return this.filterableFields().find(field => field.fieldKey === this.newFilter.fieldKey);
    }

    filterValueType(): 'date' | 'number' | 'text' {
        const type = this.selectedFilterField()?.type?.toUpperCase();
        if (type === 'DATE' || type === 'DATETIME' || type === 'TIMESTAMP') return 'date';
        if (type === 'NUMBER' || type === 'CURRENCY') return 'number';
        return 'text';
    }

    availableFilterOperators(): { label: string; value: string }[] {
        const field = this.selectedFilterField();
        return field ? this.filterOperators.filter(option => field.operators.includes(option.value)) : this.filterOperators;
    }

    onFilterFieldChange(fieldKey: string): void {
        this.newFilter.fieldKey = fieldKey;
        this.newFilter.value = null;
        this.newFilter.operator = this.availableFilterOperators()[0]?.value || 'EQ';
    }

    private dateFilterValue(date: Date): string {
        const pad = (value: number) => String(value).padStart(2, '0');
        const offsetMinutes = -date.getTimezoneOffset();
        const offset = `${offsetMinutes < 0 ? '-' : '+'}${pad(Math.floor(Math.abs(offsetMinutes) / 60))}:${pad(Math.abs(offsetMinutes) % 60)}`;
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00${offset}`;
    }

    removeFilter(index: number): void {
        this.definition.filters = this.definition.filters.filter((_, itemIndex) => itemIndex !== index);
    }

    addSort(): void {
        if (!this.newSort.fieldKey) {
            return;
        }
        this.definition.sorts = [...this.definition.sorts.filter(sort => sort.fieldKey !== this.newSort.fieldKey), { ...this.newSort }];
        this.newSort = { fieldKey: '', direction: 'ASC' };
    }

    removeSort(fieldKey: string): void {
        this.definition.sorts = this.definition.sorts.filter(sort => sort.fieldKey !== fieldKey);
    }

    addAggregate(): void {
        const field = this.aggregatableFields().find(item => item.fieldKey === this.newAggregateFieldKey);
        const functionName = this.newAggregateFunction;
        if (!field || !functionName || !field.aggregations.includes(functionName)) {
            return;
        }
        const exists = this.definition.aggregates.some(item => item.fieldKey === field.fieldKey && item.function === functionName);
        if (exists) {
            return;
        }
        this.definition.aggregates = [...this.definition.aggregates, {
            fieldKey: field.fieldKey,
            function: functionName,
            label: `${functionName} ${field.label}`
        }];
    }

    removeAggregate(index: number): void {
        this.definition.aggregates = this.definition.aggregates.filter((_, itemIndex) => itemIndex !== index);
    }

    onAggregateFieldChange(): void {
        const firstFunction = this.aggregateFunctionOptions()[0]?.value;
        if (firstFunction && !this.aggregateFunctionOptions().some(option => option.value === this.newAggregateFunction)) {
            this.newAggregateFunction = firstFunction;
        }
    }

    toggleAggregate(field: ReportingField, functionName: string, checked: boolean): void {
        const exists = this.definition.aggregates.some(item => item.fieldKey === field.fieldKey && item.function === functionName);
        if (checked && !exists) {
            this.definition.aggregates = [...this.definition.aggregates, {
                fieldKey: field.fieldKey,
                function: functionName,
                label: `${functionName} ${field.label}`
            }];
        }
        if (!checked) {
            this.definition.aggregates = this.definition.aggregates
                .filter(item => !(item.fieldKey === field.fieldKey && item.function === functionName));
        }
    }

    hasAggregate(fieldKey: string, functionName: string): boolean {
        return this.definition.aggregates.some(item => item.fieldKey === fieldKey && item.function === functionName);
    }

    aggregateFunctionOptions(): { label: string; value: string }[] {
        const field = this.aggregatableFields().find(item => item.fieldKey === this.newAggregateFieldKey);
        const aggregations = field?.aggregations?.length ? field.aggregations : ['SUM', 'AVG'];
        return aggregations.map(value => ({ label: this.titleCase(value), value }));
    }

    aggregateLabel(aggregate: ReportAggregate): string {
        return aggregate.label || `${aggregate.function} ${this.fieldLabel(aggregate.fieldKey)}`;
    }

    previewReport(): void {
        this.errorMessage = '';
        this.loading = true;
        this.reportingService.previewDefinition(this.definition)
            .pipe(finalize(() => this.loading = false))
            .subscribe({
                next: preview => {
                    this.preview = preview;
                    this.definition = preview.definition;
                    this.removeRestrictedPricingSelections();
                },
                error: error => this.errorMessage = error?.error || 'Could not preview report.'
            });
    }

    saveReport(): void {
        this.errorMessage = '';
        if (!this.ensureCompanyContext()) {
            return;
        }
        this.saving = true;
        this.reportingService.saveReport(this.definition)
            .pipe(finalize(() => this.saving = false))
            .subscribe({
                next: report => {
                    this.definition = report;
                    this.loadReports();
                    this.previewReport();
                },
                error: error => this.errorMessage = error?.error || 'Could not save report.'
            });
    }

    export(format: string): void {
        this.errorMessage = '';
        if (!this.ensureCompanyContext()) {
            return;
        }
        const actionKey = `export:${format}`;
        if (!this.reportActions.begin(actionKey)) {
            return;
        }
        this.reportingService.saveReport(this.definition)
            .pipe(
                switchMap(report => {
                    this.definition = report;
                    return this.reportingService.exportReport(report.reportId as number, format);
                }),
                finalize(() => this.reportActions.end(actionKey))
            )
            .subscribe({
                next: run => {
                    if (run.status === 'COMPLETED' && run.base64Content) {
                        this.download(run.base64Content, run.fileName, run.contentType);
                    } else {
                        this.errorMessage = run.errorMessage || 'Export failed.';
                    }
                    this.loadReports();
                },
                error: error => this.errorMessage = error?.error || 'Could not export report.'
            });
    }

    toggleRightPane(): void {
        this.rightPaneCollapsed = !this.rightPaneCollapsed;
    }

    toggleLeftPane(): void {
        this.leftPaneCollapsed = !this.leftPaneCollapsed;
    }

    moduleIcon(module: string): string {
        switch ((module || '').toLowerCase()) {
            case 'shipments': return 'pi pi-box';
            case 'clearance & forwarding': return 'pi pi-send';
            case 'accounts': return 'pi pi-wallet';
            case 'fleet': return 'pi pi-car';
            case 'crm': return 'pi pi-users';
            case 'warehousing': return 'pi pi-building';
            case 'posm': return 'pi pi-tag';
            default: return 'pi pi-database';
        }
    }

    moduleColor(module: string): string {
        switch ((module || '').toLowerCase()) {
            case 'shipments': return '#d97706';
            case 'clearance & forwarding': return '#2563eb';
            case 'accounts': return '#16a34a';
            case 'fleet': return '#9333ea';
            case 'crm': return '#e11d48';
            case 'warehousing': return '#0284c7';
            case 'posm': return '#475569';
            default: return '#1f5f74';
        }
    }

    moduleBg(module: string): string {
        switch ((module || '').toLowerCase()) {
            case 'shipments': return '#fef3c7';
            case 'clearance & forwarding': return '#dbeafe';
            case 'accounts': return '#dcfce7';
            case 'fleet': return '#f3e8ff';
            case 'crm': return '#ffe4e6';
            case 'warehousing': return '#e0f2fe';
            case 'posm': return '#f1f5f9';
            default: return '#e8f3f6';
        }
    }

    expandAllModules(): void {
        this.collapsedDatasetModules = new Set<string>();
    }

    collapseAllModules(): void {
        const allModules = new Set(this.datasets.map(d => d.module || 'General'));
        this.collapsedDatasetModules = allModules;
    }

    areAllModulesCollapsed(): boolean {
        const allModules = Array.from(new Set(this.datasets.map(d => d.module || 'General')));
        return allModules.length > 0 && allModules.every(m => this.collapsedDatasetModules.has(m));
    }

    toggleAllModules(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        if (this.areAllModulesCollapsed()) {
            this.expandAllModules();
        } else {
            this.collapseAllModules();
        }
    }

    trackByModule(_index: number, item: { module: string }): string {
        return item.module;
    }

    formatIcon(format: string): string {
        switch (format) {
            case 'pdf': return 'pi pi-file-pdf';
            case 'xlsx': return 'pi pi-file-excel';
            case 'csv': return 'pi pi-table';
            case 'docx': return 'pi pi-file-word';
            case 'pptx': return 'pi pi-desktop';
            default: return 'pi pi-file';
        }
    }

    formatColor(format: string): string {
        switch (format) {
            case 'pdf': return '#dc2626';
            case 'xlsx': return '#16a34a';
            case 'csv': return '#0284c7';
            case 'docx': return '#2563eb';
            case 'pptx': return '#d97706';
            default: return '#64748b';
        }
    }

    formatBg(format: string): string {
        switch (format) {
            case 'pdf': return '#fee2e2';
            case 'xlsx': return '#dcfce7';
            case 'csv': return '#e0f2fe';
            case 'docx': return '#dbeafe';
            case 'pptx': return '#fef3c7';
            default: return '#f1f5f9';
        }
    }

    formatTitle(format: string): string {
        switch (format) {
            case 'pdf': return 'PDF Document';
            case 'xlsx': return 'Excel Workbook';
            case 'csv': return 'CSV Data';
            case 'docx': return 'Word Document';
            case 'pptx': return 'PowerPoint';
            default: return format.toUpperCase();
        }
    }

    formatDesc(format: string): string {
        switch (format) {
            case 'pdf': return 'Formatted for print & sharing';
            case 'xlsx': return 'Full spreadsheet workbook';
            case 'csv': return 'Raw tabular text data';
            case 'docx': return 'Editable Word document';
            case 'pptx': return 'Presentation slide deck';
            default: return 'Report download';
        }
    }

    loadTemplates(): void {
        const companyId = this.companyId();
        if (!companyId) {
            this.templateErrorMessage = 'Company context is required before loading templates.';
            return;
        }
        this.templateLoading = true;
        this.reportingService.getTemplates(companyId)
            .pipe(finalize(() => this.templateLoading = false))
            .subscribe({
                next: templates => {
                    this.templates = templates;
                    if (!this.selectedTemplate && templates.length) {
                        this.openTemplate(templates[0]);
                    } else if (!templates.length) {
                        this.newTemplate();
                    }
                },
                error: error => this.templateErrorMessage = error?.error || 'Could not load templates.'
            });
    }

    templateTypes(): string[] {
        const types = new Set(this.templates.map(t => t.reportType));
        return Array.from(types).sort();
    }

    filteredTemplates(): ReportTemplate[] {
        const search = this.templateSearch.trim().toLowerCase();
        return this.templates.filter(template => {
            const matchesType = this.templateTypeFilter === 'ALL' || template.reportType === this.templateTypeFilter;
            if (!matchesType) return false;
            if (!search) return true;
            return [
                template.templateName,
                template.reportType,
                template.status,
                template.description
            ].some(value => String(value || '').toLowerCase().includes(search));
        });
    }

    filteredBindingFields(): TemplateBindingField[] {
        const fields = this.bindingFields();
        const search = this.bindingSearch.trim().toLowerCase();
        if (!search) {
            return fields;
        }
        return fields.filter(f =>
            [f.fieldLabel, f.fieldPath, f.rootName, f.javaType]
                .some(v => String(v || '').toLowerCase().includes(search))
        );
    }

    openTemplate(template: ReportTemplate): void {
        this.selectedTemplate = template;
        this.selectedReportType = template.reportType;
        this.templateName = template.templateName;
        this.templateDescription = template.description || '';
        this.templateHtml = template.latestVersion?.templateHtml || this.defaultTemplateHtml();
        this.sampleContextText = template.latestVersion?.sampleContextJson || '{}';
        this.templatePreview = undefined;
        this.setEditorValue(this.templateHtml);
        this.loadTemplateVersions(template.templateId);
        this.loadBindingCatalog(template.reportType);
    }

    newTemplate(): void {
        this.selectedTemplate = undefined;
        this.templateVersions = [];
        this.templatePreview = undefined;
        this.templateName = this.titleCase(this.selectedReportType);
        this.templateDescription = '';
        this.templateHtml = this.defaultTemplateHtml();
        this.sampleContextText = '{}';
        this.setEditorValue(this.templateHtml);
        this.loadBindingCatalog(this.selectedReportType);
    }

    onTemplateReportTypeChange(reportType: string): void {
        this.selectedReportType = reportType;
        if (!this.selectedTemplate) {
            this.templateName = this.titleCase(reportType);
        }
        this.loadBindingCatalog(reportType);
    }

    loadTemplateVersions(templateId: number): void {
        this.reportingService.getTemplateVersions(templateId).subscribe({
            next: versions => this.templateVersions = versions,
            error: () => this.templateVersions = []
        });
    }

    loadBindingCatalog(reportType: string): void {
        this.reportingService.getTemplateBindings(reportType).subscribe({
            next: catalog => {
                this.bindingCatalog = catalog;
                if ((!this.sampleContextText || this.sampleContextText === '{}') && catalog.sampleContext) {
                    this.sampleContextText = JSON.stringify(catalog.sampleContext, null, 2);
                }
            },
            error: () => this.bindingCatalog = undefined
        });
    }

    saveTemplate(): void {
        if (this.selectedTemplate && !this.canEditTemplates) {
            return;
        }
        if (!this.selectedTemplate && !this.canCreateTemplates) {
            return;
        }
        const companyId = this.companyId();
        if (!companyId) {
            this.templateErrorMessage = 'Company context is required before saving templates.';
            return;
        }
        if (!this.validateSampleJson(false)) {
            return;
        }
        this.templateSaving = true;
        this.templateErrorMessage = '';
        this.reportingService.saveTemplate({
            companyId,
            reportType: this.selectedReportType,
            templateName: this.templateName || this.titleCase(this.selectedReportType),
            description: this.templateDescription,
            templateHtml: this.templateHtml,
            sampleContextJson: this.sampleContextText
        }, this.selectedTemplate?.templateId)
            .pipe(finalize(() => this.templateSaving = false))
            .subscribe({
                next: template => {
                    this.selectedTemplate = template;
                    this.openTemplate(template);
                    this.loadTemplates();
                },
                error: error => this.templateErrorMessage = error?.error || 'Could not save template.'
            });
    }

    validateTemplate(): void {
        if (!this.canPreviewTemplates && !this.canEditTemplates) {
            return;
        }
        if (!this.selectedTemplate?.templateId) {
            this.saveTemplate();
            return;
        }
        this.templateLoading = true;
        this.templateErrorMessage = '';
        this.reportingService.validateTemplate(this.selectedTemplate.templateId, this.selectedTemplate.activeVersionId)
            .pipe(finalize(() => this.templateLoading = false))
            .subscribe({
                next: response => {
                    this.templatePreview = response;
                    this.loadTemplates();
                },
                error: error => this.templateErrorMessage = error?.error || 'Could not validate template.'
            });
    }

    previewTemplate(outputMode: 'HTML' | 'PDF' = this.templatePreviewMode): void {
        if (!this.canPreviewTemplates) {
            return;
        }
        const context = this.validateSampleJson(true);
        if (!context) {
            return;
        }
        const actionKey = `template-preview:${outputMode}`;
        if (!this.reportActions.begin(actionKey)) {
            return;
        }
        this.templatePreviewMode = outputMode;
        this.templateLoading = true;
        this.templateErrorMessage = '';
        this.reportingService.previewTemplate({
            templateId: this.selectedTemplate?.templateId,
            templateVersionId: this.selectedTemplate?.activeVersionId,
            companyId: this.companyId(),
            reportType: this.selectedReportType,
            templateHtml: this.templateHtml,
            context,
            outputMode
        })
            .pipe(finalize(() => {
                this.templateLoading = false;
                this.reportActions.end(actionKey);
            }))
            .subscribe({
                next: response => {
                    this.templatePreview = response;
                    if (outputMode === 'PDF' && response.base64Pdf) {
                        this.download(response.base64Pdf, `${this.slug(this.templateName)}.pdf`, 'application/pdf');
                    }
                },
                error: error => this.templateErrorMessage = error?.error || 'Could not preview template.'
            });
    }

    publishTemplate(): void {
        if (!this.canPublishTemplates) {
            return;
        }
        if (!this.selectedTemplate?.templateId) {
            this.templateErrorMessage = 'Save the template before publishing.';
            return;
        }
        this.templateLoading = true;
        this.templateErrorMessage = '';
        this.reportingService.publishTemplate(this.selectedTemplate.templateId, this.selectedTemplate.activeVersionId)
            .pipe(finalize(() => this.templateLoading = false))
            .subscribe({
                next: template => {
                    this.openTemplate(template);
                    this.loadTemplates();
                },
                error: error => this.templateErrorMessage = error?.error || 'Could not publish template.'
            });
    }

    restoreVersion(version: ReportTemplateVersion): void {
        if (!this.canEditTemplates) {
            return;
        }
        if (!this.selectedTemplate?.templateId) {
            return;
        }
        this.templateLoading = true;
        this.reportingService.restoreTemplateVersion(this.selectedTemplate.templateId, version.templateVersionId)
            .pipe(finalize(() => this.templateLoading = false))
            .subscribe({
                next: template => {
                    this.openTemplate(template);
                    this.loadTemplates();
                },
                error: error => this.templateErrorMessage = error?.error || 'Could not restore template version.'
            });
    }

    insertBinding(field: TemplateBindingField): void {
        const expression = field.fieldPath ? `\${${field.fieldPath}}` : `\${${field.rootName}}`;
        const snippet = ` th:text="${expression}"`;
        if (this.templateEditor) {
            const selection = this.templateEditor.getSelection();
            this.templateEditor.executeEdits('binding-insert', [{ range: selection, text: snippet, forceMoveMarkers: true }]);
            this.templateHtml = this.templateEditor.getValue();
            return;
        }
        this.templateHtml += snippet;
    }

    diagnostics(): string {
        return (this.templatePreview?.diagnostics || [])
            .map(item => `${item.severity}: ${item.message}`)
            .join('\n');
    }

    bindingFields(): TemplateBindingField[] {
        return this.bindingCatalog?.fields || [];
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const value = String(status || '').toUpperCase();
        if (value === 'PUBLISHED' || value === 'OK') return 'success';
        if (value === 'VALIDATED') return 'info';
        if (value === 'FAILED') return 'danger';
        return 'warn';
    }

    fieldLabel(fieldKey: string): string {
        return this.selectedDataset?.fields.find(field => field.fieldKey === fieldKey)?.label ?? fieldKey;
    }

    fieldPath(field: ReportingField): string {
        return field.path || field.fieldKey;
    }

    fieldKind(field: ReportingField): string {
        return (field.fieldKind || 'SCALAR').toUpperCase();
    }

    fieldIndent(field: ReportingField): string {
        const depth = Math.max(0, (field.depth || 1) - 1);
        return `${depth * 0.95}rem`;
    }

    isSelectableField(field: ReportingField): boolean {
        return !!field
            && field.visible
            && !field.array
            && this.fieldKind(field) === 'SCALAR'
            && !this.isRestrictedPricingField(field);
    }

    isQueryableField(field: ReportingField): boolean {
        return this.isSelectableField(field) && field.queryable !== false;
    }

    fieldPickerFields(): ReportingField[] {
        const fields = (this.selectedDataset?.fields || [])
            .filter(field => !this.isRestrictedPricingField(field));
        const search = this.fieldSearch.trim().toLowerCase();
        if (!search) {
            return fields.filter(field => this.isTreeFieldVisible(field, fields));
        }
        return fields.filter(field => this.isSearchBranchVisible(field, fields, search));
    }

    hasFieldChildren(field: ReportingField): boolean {
        const path = this.fieldPath(field);
        return (this.selectedDataset?.fields || []).some(item => item.parentPath === path && !this.isRestrictedPricingField(item));
    }

    isFieldExpanded(field: ReportingField): boolean {
        return this.expandedFieldPaths.has(this.fieldPath(field));
    }

    toggleFieldExpanded(field: ReportingField, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        const path = this.fieldPath(field);
        const next = new Set(this.expandedFieldPaths);
        if (next.has(path)) {
            next.delete(path);
        } else {
            next.add(path);
        }
        this.expandedFieldPaths = next;
    }

    private isTreeFieldVisible(field: ReportingField, fields: ReportingField[]): boolean {
        let parentPath = field.parentPath;
        while (parentPath) {
            if (!this.expandedFieldPaths.has(parentPath)) {
                return false;
            }
            parentPath = fields.find(item => this.fieldPath(item) === parentPath)?.parentPath;
        }
        return true;
    }

    private isSearchBranchVisible(field: ReportingField, fields: ReportingField[], search: string): boolean {
        const path = this.fieldPath(field);
        if (this.fieldMatchesSearch(field, search)) {
            return true;
        }
        return fields.some(item => this.fieldMatchesSearch(item, search)
            && (this.isAncestorPath(path, this.fieldPath(item)) || this.isAncestorPath(this.fieldPath(item), path)));
    }

    private fieldMatchesSearch(field: ReportingField, search: string): boolean {
        return [
            field.label,
            field.fieldKey,
            field.elementName,
            field.groupKey,
            field.groupLabel,
            field.path,
            field.description,
            field.type,
            this.fieldKind(field),
            this.elementName(field)
        ].some(value => String(value || '').toLowerCase().includes(search));
    }

    private isAncestorPath(parentPath: string, childPath: string): boolean {
        return !!parentPath && !!childPath && childPath.startsWith(`${parentPath}.`);
    }

    filterableFields(): ReportingField[] {
        return (this.selectedDataset?.fields || []).filter(field =>
            this.isQueryableField(field) && field.filterable);
    }

    sortableFields(): ReportingField[] {
        return (this.selectedDataset?.fields || []).filter(field =>
            this.isQueryableField(field) && field.sortable);
    }

    aggregatableFields(): ReportingField[] {
        return (this.selectedDataset?.fields || []).filter(field =>
            this.isSelectableField(field) && field.aggregations?.length);
    }

    selectedColumns(): ReportColumn[] {
        return this.definition.columns || [];
    }

    datasetModules(): Array<{ module: string; datasets: ReportingDataset[] }> {
        const search = this.catalogSearch.trim().toLowerCase();
        const grouped = new Map<string, ReportingDataset[]>();
        for (const dataset of this.datasets) {
            if (search) {
                const datasetMatches = [
                    dataset.label,
                    dataset.datasetKey,
                    dataset.module,
                    dataset.description
                ].some(value => String(value || '').toLowerCase().includes(search));
                const fieldMatches = (dataset.fields || []).some(field => this.fieldMatchesSearch(field, search));
                if (!datasetMatches && !fieldMatches) {
                    continue;
                }
            }
            const moduleName = dataset.module || 'General';
            const list = grouped.get(moduleName) || [];
            list.push(dataset);
            grouped.set(moduleName, list);
        }
        return Array.from(grouped.entries()).map(([module, datasets]) => ({ module, datasets }));
    }

    toggleTreeSection(sectionKey: string): void {
        const next = new Set(this.collapsedTreeSections);
        if (next.has(sectionKey)) {
            next.delete(sectionKey);
        } else {
            next.add(sectionKey);
        }
        this.collapsedTreeSections = next;
    }

    isTreeSectionCollapsed(sectionKey: string): boolean {
        return this.collapsedTreeSections.has(sectionKey);
    }

    toggleDatasetModule(moduleName: string, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const next = new Set(this.collapsedDatasetModules);
        if (next.has(moduleName)) {
            next.delete(moduleName);
        } else {
            next.add(moduleName);
        }
        this.collapsedDatasetModules = next;
    }

    isDatasetModuleCollapsed(moduleName: string): boolean {
        if (this.catalogSearch && this.catalogSearch.trim()) {
            return false;
        }
        return this.collapsedDatasetModules.has(moduleName);
    }

    elementName(field: ReportingField): string {
        if (field.elementName) {
            return field.elementName;
        }
        return (field.fieldKey || 'ELEMENT')
            .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
            .replace(/[^A-Za-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .toUpperCase();
    }

    fieldGroupKey(field: ReportingField): string {
        if (field.groupKey) {
            return field.groupKey;
        }
        const type = (field.type || 'TEXT').toUpperCase();
        const token = `${field.fieldKey || ''} ${field.path || ''}`.toLowerCase();
        if (type === 'CURRENCY' || type === 'NUMBER'
            || ['amount', 'total', 'qty', 'quantity', 'weight', 'volume', 'cost', 'revenue', 'margin', 'balance', 'rate', 'score', 'count'].some(k => token.includes(k))) {
            return 'G_4';
        }
        if (type === 'DATE'
            || ['date', 'time', 'eta', 'etd', 'arrival', 'departure', 'city', 'country', 'port', 'address', 'origin', 'destination', 'warehouse', 'zone', 'bin'].some(k => token.includes(k))) {
            return 'G_3';
        }
        if (field.depth && field.depth > 1
            || ['customer', 'client', 'supplier', 'vendor', 'party', 'driver', 'vehicle', 'employee', 'worker', 'owner', 'account', 'item', 'sku', 'product', 'carrier', 'vessel', 'project', 'contract'].some(k => token.includes(k))) {
            return 'G_2';
        }
        return 'G_1';
    }

    fieldGroupLabel(field: ReportingField): string {
        if (field.groupLabel) {
            return field.groupLabel;
        }
        const key = this.fieldGroupKey(field);
        if (key === 'G_4') return 'G_4 · Measures, Amounts & Quantities';
        if (key === 'G_3') return 'G_3 · Dates, Schedule & Locations';
        if (key === 'G_2') return 'G_2 · Parties, Resources & Master Data';
        return 'G_1 · Core Header & Status';
    }

    dataTypeBadge(field: ReportingField): string {
        const type = (field.type || 'TEXT').toUpperCase();
        if (type === 'CURRENCY') return 'SAR';
        if (type === 'NUMBER') return '999';
        if (type === 'DATE') return 'DATE';
        if (type === 'ENUM') return 'ENUM';
        return 'abc';
    }

    dataTypeClass(field: ReportingField): string {
        const type = (field.type || 'TEXT').toUpperCase();
        if (type === 'CURRENCY') return 'type-currency';
        if (type === 'NUMBER') return 'type-number';
        if (type === 'DATE') return 'type-date';
        if (type === 'ENUM') return 'type-enum';
        return 'type-text';
    }

    dataGroups(): ReportingDataGroup[] {
        const selectableFields = (this.selectedDataset?.fields || [])
            .filter(field => this.isSelectableField(field));
        const groupsMap = new Map<string, { groupLabel: string; fields: ReportingField[] }>();

        for (const field of selectableFields) {
            const groupKey = this.fieldGroupKey(field);
            const groupLabel = this.fieldGroupLabel(field);
            const existing = groupsMap.get(groupKey);
            if (existing) {
                if (existing.groupLabel.startsWith(`${groupKey} · `) && groupLabel && !groupLabel.includes('Core Header') && !groupLabel.includes('Parties, Resources')) {
                    existing.groupLabel = groupLabel;
                }
                existing.fields.push(field);
            } else {
                groupsMap.set(groupKey, {
                    groupLabel,
                    fields: [field]
                });
            }
        }

        return Array.from(groupsMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupKey, entry]) => {
                const shortTitle = entry.groupLabel.includes('·')
                    ? entry.groupLabel.split('·').slice(1).join('·').trim()
                    : entry.groupLabel;
                const fieldKeysInGroup = new Set(entry.fields.map(f => f.fieldKey));
                const selectedCount = entry.fields.filter(f => this.isColumnSelected(f.fieldKey)).length;
                const aggregates = (this.definition.aggregates || [])
                    .filter(agg => fieldKeysInGroup.has(agg.fieldKey));
                return {
                    groupKey,
                    groupLabel: entry.groupLabel,
                    shortTitle,
                    fields: entry.fields,
                    selectedCount,
                    totalCount: entry.fields.length,
                    aggregates
                };
            });
    }

    visibleDataGroups(): ReportingDataGroup[] {
        const search = this.fieldSearch.trim().toLowerCase();
        return this.dataGroups()
            .filter(group => this.activeGroupFilter === 'ALL' || group.groupKey === this.activeGroupFilter)
            .map(group => {
                if (!search) {
                    return group;
                }
                const filteredFields = group.fields.filter(field => this.fieldMatchesSearch(field, search));
                return {
                    ...group,
                    fields: filteredFields
                };
            })
            .filter(group => group.fields.length > 0 || !search);
    }

    structureFields(): ReportingField[] {
        return this.visibleDataGroups().flatMap(group => group.fields);
    }

    isDataGroupCollapsed(groupKey: string): boolean {
        return this.collapsedDataGroups.has(groupKey);
    }

    toggleDataGroupCollapsed(groupKey: string, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        const next = new Set(this.collapsedDataGroups);
        if (next.has(groupKey)) {
            next.delete(groupKey);
        } else {
            next.add(groupKey);
        }
        this.collapsedDataGroups = next;
    }

    focusDataGroup(groupKey: string): void {
        this.activeGroupFilter = this.activeGroupFilter === groupKey ? 'ALL' : groupKey;
        const next = new Set(this.collapsedDataGroups);
        next.delete(groupKey);
        this.collapsedDataGroups = next;
    }

    toggleGroupSelection(group: ReportingDataGroup, checked: boolean, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        const groupKeys = new Set(group.fields.map(field => field.fieldKey));
        if (checked) {
            const additions = group.fields
                .map(field => field.fieldKey)
                .filter(key => !this.selectedFieldKeys.includes(key));
            this.selectedFieldKeys = [...this.selectedFieldKeys, ...additions];
        } else {
            this.selectedFieldKeys = this.selectedFieldKeys.filter(key => !groupKeys.has(key));
        }
        this.syncDefinitionColumnsFromSelectedKeys();
    }

    selectRecommendedColumns(): void {
        if (!this.selectedDataset) {
            return;
        }
        const selectableFields = this.selectedDataset.fields.filter(field => this.isSelectableField(field));
        const recommended = selectableFields.filter(field => field.defaultSelected);
        const chosen = recommended.length ? recommended : selectableFields.slice(0, 8);
        this.selectedFieldKeys = chosen.map(field => field.fieldKey);
        this.syncDefinitionColumnsFromSelectedKeys();
    }

    selectAllColumns(): void {
        if (!this.selectedDataset) {
            return;
        }
        const selectableFields = this.selectedDataset.fields.filter(field => this.isSelectableField(field));
        this.selectedFieldKeys = selectableFields.map(field => field.fieldKey);
        this.syncDefinitionColumnsFromSelectedKeys();
    }

    clearAllColumns(): void {
        this.selectedFieldKeys = [];
        this.definition.columns = [];
    }

    isFieldFiltered(fieldKey: string): boolean {
        return (this.definition.filters || []).some(filter => filter.fieldKey === fieldKey);
    }

    fieldSortDirection(fieldKey: string): string {
        return (this.definition.sorts || []).find(sort => sort.fieldKey === fieldKey)?.direction || '';
    }

    fieldAggregates(fieldKey: string): ReportAggregate[] {
        return (this.definition.aggregates || []).filter(agg => agg.fieldKey === fieldKey);
    }

    quickFilterField(field: ReportingField, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        if (!field.filterable) {
            return;
        }
        this.newFilter = {
            fieldKey: field.fieldKey,
            operator: 'EQ',
            value: null
        };
        this.onFilterFieldChange(field.fieldKey);
        this.activeRuleTab = 'filters';
    }

    quickToggleSort(field: ReportingField, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        if (!field.sortable) {
            return;
        }
        const current = this.fieldSortDirection(field.fieldKey);
        if (!current) {
            this.definition.sorts = [...this.definition.sorts, { fieldKey: field.fieldKey, direction: 'ASC' }];
        } else if (current === 'ASC') {
            this.definition.sorts = this.definition.sorts.map(sort =>
                sort.fieldKey === field.fieldKey ? { ...sort, direction: 'DESC' } : sort);
        } else {
            this.removeSort(field.fieldKey);
        }
    }

    quickAddFieldAggregate(field: ReportingField, fn?: string, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        const allowed = field.aggregations?.length ? field.aggregations : ['COUNT'];
        const defaultFn = fn || (field.type === 'CURRENCY' || field.type === 'NUMBER'
            ? (allowed.includes('SUM') ? 'SUM' : allowed[0])
            : (allowed.includes('COUNT') ? 'COUNT' : allowed[0]));
        if (!defaultFn) {
            return;
        }
        const exists = (this.definition.aggregates || []).some(
            item => item.fieldKey === field.fieldKey && item.function === defaultFn
        );
        if (!exists) {
            this.definition.aggregates = [
                ...(this.definition.aggregates || []),
                {
                    fieldKey: field.fieldKey,
                    function: defaultFn,
                    label: `${defaultFn}(${this.elementName(field)})`
                }
            ];
        }
    }

    changeAggregateFunction(aggregate: ReportAggregate, newFunction: string): void {
        const field = this.selectedDataset?.fields.find(f => f.fieldKey === aggregate.fieldKey);
        const elemName = field ? this.elementName(field) : aggregate.fieldKey.toUpperCase();
        this.definition.aggregates = (this.definition.aggregates || []).map(item => {
            if (item === aggregate || (item.fieldKey === aggregate.fieldKey && item.function === aggregate.function)) {
                return {
                    ...item,
                    function: newFunction,
                    label: `${newFunction}(${elemName})`
                };
            }
            return item;
        });
    }

    removeGroupAggregate(aggregate: ReportAggregate, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.definition.aggregates = (this.definition.aggregates || []).filter(
            item => !(item.fieldKey === aggregate.fieldKey && item.function === aggregate.function)
        );
    }

    onFieldDragStart(field: ReportingField, event: DragEvent): void {
        this.draggedField = field;
        if (event.dataTransfer) {
            event.dataTransfer.setData('text/plain', field.fieldKey);
            event.dataTransfer.effectAllowed = 'copy';
        }
    }

    onFieldDragEnd(): void {
        this.draggedField = undefined;
        this.dragOverGroupKey = '';
    }

    onGroupDropZoneDragOver(groupKey: string, event: DragEvent): void {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
        this.dragOverGroupKey = groupKey;
    }

    onGroupDropZoneDragLeave(groupKey: string): void {
        if (this.dragOverGroupKey === groupKey) {
            this.dragOverGroupKey = '';
        }
    }

    onGroupDropZoneDrop(group: ReportingDataGroup, event: DragEvent): void {
        event.preventDefault();
        this.dragOverGroupKey = '';
        const fieldKey = this.draggedField?.fieldKey || event.dataTransfer?.getData('text/plain') || '';
        const field = this.selectedDataset?.fields.find(item => item.fieldKey === fieldKey);
        this.draggedField = undefined;
        if (!field) {
            return;
        }
        this.quickAddFieldAggregate(field);
    }

    addGroupDefaultSummary(group: ReportingDataGroup, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        const candidate = group.fields.find(field =>
            (field.type === 'CURRENCY' || field.type === 'NUMBER')
            && !(this.definition.aggregates || []).some(agg => agg.fieldKey === field.fieldKey)
        ) || group.fields.find(field =>
            !(this.definition.aggregates || []).some(agg => agg.fieldKey === field.fieldKey)
        ) || group.fields[0];
        if (candidate) {
            this.quickAddFieldAggregate(candidate);
        }
    }

    columnLabel(fieldKey: string): string {
        return this.definition.columns.find(col => col.fieldKey === fieldKey)?.label || this.fieldLabel(fieldKey);
    }

    updateColumnLabel(fieldKey: string, label: string): void {
        this.definition.columns = this.definition.columns.map(col =>
            col.fieldKey === fieldKey ? { ...col, label } : col
        );
    }

    dataModelCodePreview(): string {
        const dataset = this.selectedDataset;
        if (!dataset) {
            return '<dataModel name="Empty"/>';
        }
        const groupsXml = this.dataGroups().map(group => {
            const elementsXml = group.fields
                .filter(field => this.isColumnSelected(field.fieldKey))
                .map(field =>
                    `      <element name="${this.elementName(field)}" value="${this.fieldPath(field)}" label="${this.columnLabel(field.fieldKey)}" dataType="${field.type}" />`
                )
                .join('\n');
            const summariesXml = group.aggregates
                .map(agg => {
                    const f = dataset.fields.find(item => item.fieldKey === agg.fieldKey);
                    const elem = f ? this.elementName(f) : agg.fieldKey.toUpperCase();
                    return `      <element name="${agg.function}_${elem}" function="${agg.function}()" value="${ group.groupKey }.${elem}" label="${agg.label || (agg.function + ' ' + elem)}" />`;
                })
                .join('\n');
            return [
                `    <group name="${group.groupKey}" label="${group.groupLabel}" source="${dataset.datasetKey}">`,
                elementsXml || '      <!-- No elements selected in this group -->',
                summariesXml,
                `    </group>`
            ].filter(Boolean).join('\n');
        }).join('\n');

        return [
            `<?xml version="1.0" encoding="UTF-8"?>`,
            `<dataModel xmlns="http://xmlns.oracle.com/oxp/xmlp" name="${this.definition.reportName || dataset.label}" module="${dataset.module}">`,
            `  <dataSets>`,
            `    <dataSet name="${dataset.datasetKey}" type="ERP_DOMAIN_DATASET" label="${dataset.label}" />`,
            `  </dataSets>`,
            `  <output rootName="DATA_DS" uniqueRowName="false">`,
            groupsXml,
            `  </output>`,
            `</dataModel>`
        ].join('\n');
    }

    private syncDefinitionColumnsFromSelectedKeys(): void {
        const existingLabels = new Map(
            (this.definition.columns || []).map(col => [col.fieldKey, col.label])
        );
        this.definition.columns = this.selectedFieldKeys
            .map(key => this.selectedDataset?.fields.find(field => field.fieldKey === key))
            .filter((field): field is ReportingField => !!field)
            .map(field => {
                const col = this.columnFromField(field);
                const customLabel = existingLabels.get(field.fieldKey);
                if (customLabel) {
                    col.label = customLabel;
                }
                return col;
            });
    }

    cell(row: Record<string, unknown>, fieldKey: string): string {
        const value = row[fieldKey];
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        if (this.isDateField(fieldKey)) {
            const formattedDate = this.formatDateValue(value);
            if (formattedDate) {
                return formattedDate;
            }
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T|\s)\d{2}:\d{2}/.test(value)) {
            const formattedDate = this.formatDateValue(value);
            if (formattedDate) {
                return formattedDate;
            }
        }
        const field = this.selectedDataset?.fields?.find(f => f.fieldKey === fieldKey);
        if (field && (field.type === 'CURRENCY' || field.type === 'NUMBER') && typeof value === 'number') {
            return value.toLocaleString(undefined, {
                minimumFractionDigits: field.type === 'CURRENCY' ? 2 : 0,
                maximumFractionDigits: 2
            });
        }
        return String(value);
    }

    totalCell(fieldKey: string): string {
        const value = this.preview?.result?.totals?.[fieldKey];
        if (value === null || value === undefined || value === '') {
            return '';
        }
        if (this.isDateField(fieldKey)) {
            const formatted = this.formatDateValue(value);
            if (formatted) return formatted;
        }
        if (typeof value === 'number') {
            const field = this.selectedDataset?.fields?.find(f => f.fieldKey === fieldKey);
            return value.toLocaleString(undefined, {
                minimumFractionDigits: field?.type === 'CURRENCY' ? 2 : 0,
                maximumFractionDigits: 2
            });
        }
        return String(value);
    }

    formatDateValue(value: unknown): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        let date: Date | null = null;

        if (value instanceof Date) {
            date = value;
        } else if (typeof value === 'number') {
            if (isNaN(value) || value <= 0) return null;
            // Epoch seconds (< 1e11) vs milliseconds (>= 1e11)
            const ms = value < 1e11 ? value * 1000 : value;
            date = new Date(ms);
        } else if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;

            // Handle scientific notation or numeric strings like "1.789453838E9" or "1789453838"
            if (/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(trimmed)) {
                const num = Number(trimmed);
                if (!isNaN(num) && num > 0) {
                    const ms = num < 1e11 ? num * 1000 : num;
                    date = new Date(ms);
                }
            } else {
                const parsed = Date.parse(trimmed);
                if (!isNaN(parsed)) {
                    date = new Date(parsed);
                }
            }
        }

        if (!date || isNaN(date.getTime())) {
            return null;
        }

        const day = String(date.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const year = date.getFullYear();

        const hasTime = (date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0);
        if (hasTime) {
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}-${month}-${year} ${hours}:${minutes}`;
        }
        return `${day}-${month}-${year}`;
    }

    private isDateField(fieldKey: string): boolean {
        if (!fieldKey) return false;
        const field = this.selectedDataset?.fields?.find(f => f.fieldKey === fieldKey);
        if (field) {
            const type = (field.type || '').toUpperCase();
            if (type === 'DATE' || type === 'TIMESTAMP' || type === 'DATETIME') {
                return true;
            }
        }
        const col = this.definition?.columns?.find(c => c.fieldKey === fieldKey);
        if (col && (col.format === 'date' || col.format === 'timestamp')) {
            return true;
        }
        const lower = fieldKey.toLowerCase();
        return lower.includes('date') ||
               lower.includes('arrival') ||
               lower.includes('departure') ||
               lower.endsWith('time') ||
               lower.endsWith('at') ||
               lower.includes('updated') ||
               lower.includes('created') ||
               lower.includes('deadline') ||
               lower.includes('schedule');
    }

    severity(value: unknown): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const normalized = String(value ?? '').toUpperCase();
        if (normalized.includes('COMPLETED') || normalized.includes('POSTED') || normalized.includes('PAID') || normalized.includes('ACTIVE')) return 'success';
        if (normalized.includes('DELAY') || normalized.includes('APPROVED')) return 'warn';
        if (normalized.includes('HOLD') || normalized.includes('FAILED')) return 'danger';
        return 'info';
    }

    trackDataset(_: number, dataset: ReportingDataset): string {
        return dataset.datasetKey;
    }

    trackField(_: number, field: ReportingField): string {
        return field.fieldKey;
    }

    trackColumn(_: number, column: ReportColumn): string {
        return column.fieldKey;
    }

    trackDataGroup(_: number, group: ReportingDataGroup): string {
        return group.groupKey;
    }

    private applyDataset(dataset: ReportingDataset): void {
        this.selectedDataset = dataset;
        const activeModule = dataset.module || 'General';
        if (this.collapsedDatasetModules.has(activeModule)) {
            const next = new Set(this.collapsedDatasetModules);
            next.delete(activeModule);
            this.collapsedDatasetModules = next;
        }
        this.fieldSearch = '';
        this.activeGroupFilter = 'ALL';
        this.collapsedDataGroups = new Set<string>();
        this.expandedFieldPaths = new Set<string>();
        this.definition = this.emptyDefinition(dataset.datasetKey, `${dataset.label} Report`);
        const selectableFields = dataset.fields.filter(field => this.isSelectableField(field));
        const recommendedFields = selectableFields.filter(field => field.defaultSelected);
        const initialFields = recommendedFields.length ? recommendedFields : selectableFields.slice(0, 8);
        this.definition.columns = initialFields.map(field => this.columnFromField(field));
        this.selectedFieldKeys = this.definition.columns.map(column => column.fieldKey);
        this.definition.aggregates = [];
        this.newAggregateFieldKey = this.aggregatableFields()[0]?.fieldKey || '';
        this.newAggregateFunction = this.aggregateFunctionOptions()[0]?.value || 'SUM';
    }

    private emptyDefinition(datasetKey: string, reportName = 'Shipment Operations Report'): ReportDefinition {
        return {
            companyId: this.companyId(),
            reportName,
            datasetKey,
            columns: [],
            filters: [],
            sorts: [],
            groups: [],
            aggregates: [],
            layout: {
                pageSize: 'A4',
                orientation: 'LANDSCAPE',
                showFilters: true,
                showTotals: true,
                repeatHeader: true
            },
            branding: {
                title: reportName,
                subtitle: 'Self-service report generated by CargoMate',
                primaryColor: '#1F5F74'
            },
            exportOptions: {
                formats: [...this.formats],
                maxRows: 5000
            }
        };
    }

    private columnFromField(field: ReportingField): ReportColumn {
        return {
            fieldKey: field.fieldKey,
            label: field.label,
            alignment: field.type === 'NUMBER' || field.type === 'CURRENCY' ? 'RIGHT' : 'LEFT',
            format: field.type.toLowerCase()
        };
    }

    private removeRestrictedPricingSelections(): void {
        if (!this.selectedDataset) {
            this.selectedFieldKeys = [];
            return;
        }
        this.definition.columns = (this.definition.columns || [])
            .filter(column => {
                const field = this.selectedDataset?.fields.find(item => item.fieldKey === column.fieldKey);
                return field ? !this.isRestrictedPricingField(field) : true;
            });
        this.definition.filters = (this.definition.filters || [])
            .filter(filter => {
                const field = this.selectedDataset?.fields.find(item => item.fieldKey === filter.fieldKey);
                return field ? !this.isRestrictedPricingField(field) : true;
            });
        this.definition.sorts = (this.definition.sorts || [])
            .filter(sort => {
                const field = this.selectedDataset?.fields.find(item => item.fieldKey === sort.fieldKey);
                return field ? !this.isRestrictedPricingField(field) : true;
            });
        this.definition.aggregates = (this.definition.aggregates || [])
            .filter(aggregate => {
                const field = this.selectedDataset?.fields.find(item => item.fieldKey === aggregate.fieldKey);
                return field ? !this.isRestrictedPricingField(field) : true;
            });
        this.selectedFieldKeys = this.definition.columns.map(column => column.fieldKey);
    }

    private isRestrictedPricingField(field: ReportingField): boolean {
        if (this.canViewShipmentPricing) {
            return false;
        }
        const key = `${field.fieldKey} ${field.path || ''} ${field.label || ''} ${field.type || ''}`.toLowerCase();
        return field.type === 'CURRENCY'
            || ['price', 'charge', 'revenue', 'cost', 'margin', 'fare', 'worth', 'amount', 'buying', 'selling', 'invoice', 'bill', 'ar'].some(token => key.includes(token));
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private ensureCompanyContext(): boolean {
        const companyId = this.companyId();
        if (!companyId) {
            this.errorMessage = 'Company context is required before saving or exporting reports.';
            return false;
        }
        this.definition.companyId = companyId;
        return true;
    }

    private cloneReport(report: ReportDefinition): ReportDefinition {
        return JSON.parse(JSON.stringify(report)) as ReportDefinition;
    }

    private download(base64: string, fileName: string, contentType: string): void {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: contentType });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    private validateSampleJson(returnValue: true): unknown | null;
    private validateSampleJson(returnValue: false): boolean;
    private validateSampleJson(returnValue: boolean): unknown | null | boolean {
        try {
            const value = this.sampleContextText?.trim() ? JSON.parse(this.sampleContextText) : {};
            return returnValue ? value : true;
        } catch (error) {
            this.templateErrorMessage = 'Sample context JSON is invalid.';
            return returnValue ? null : false;
        }
    }

    private async initTemplateEditor(): Promise<void> {
        if (this.templateEditorInitializing || this.templateEditor || !this.templateEditorHost?.nativeElement) {
            this.templateEditor?.layout?.();
            return;
        }
        this.templateEditorInitializing = true;
        try {
            const monaco = await this.loadMonaco();
            this.templateEditor = monaco.editor.create(this.templateEditorHost.nativeElement, {
                value: this.templateHtml,
                language: 'html',
                theme: 'vs',
                automaticLayout: true,
                minimap: { enabled: false },
                fontSize: 13,
                wordWrap: 'on',
                scrollBeyondLastLine: false
            });
            this.templateEditor.onDidChangeModelContent(() => {
                this.templateHtml = this.templateEditor.getValue();
            });
        } catch {
            this.templateEditorFallback = true;
        } finally {
            this.templateEditorInitializing = false;
        }
    }

    private loadMonaco(): Promise<any> {
        const browserWindow = window as any;
        if (browserWindow.monaco?.editor) {
            return Promise.resolve(browserWindow.monaco);
        }

        if (ReportsWorkspaceComponent.monacoLoaderPromise) {
            return ReportsWorkspaceComponent.monacoLoaderPromise;
        }

        ReportsWorkspaceComponent.monacoLoaderPromise = new Promise((resolve, reject) => {
            const monacoBasePath = new URL('assets/monaco/vs', document.baseURI).toString().replace(/\/$/, '');
            const startEditor = () => {
                browserWindow.require.config({ paths: { vs: monacoBasePath } });
                browserWindow.require(['vs/editor/editor.main'], () => resolve(browserWindow.monaco), reject);
            };

            if (browserWindow.require) {
                startEditor();
                return;
            }

            const script = document.createElement('script');
            script.src = `${monacoBasePath}/loader.js`;
            script.onload = () => startEditor();
            script.onerror = () => reject(new Error('Could not load the Monaco editor.'));
            document.body.appendChild(script);
        });

        return ReportsWorkspaceComponent.monacoLoaderPromise;
    }

    private setEditorValue(value: string): void {
        if (this.templateEditor) {
            this.templateEditor.setValue(value);
        }
        setTimeout(() => this.initTemplateEditor());
    }

    private sqlPreviewRequest(includeParameters: boolean): SqlReportPreviewRequest | null {
        const companyId = this.companyId();
        if (!companyId) {
            this.sqlErrorMessage = 'Company context is required.';
            return null;
        }
        let parameters: Record<string, unknown> = {};
        if (includeParameters) {
            try {
                const parsed = this.sqlParametersText.trim() ? JSON.parse(this.sqlParametersText) : {};
                if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
                    throw new Error('Parameters must be a JSON object.');
                }
                parameters = parsed as Record<string, unknown>;
            } catch {
                this.sqlErrorMessage = 'SQL parameters must be a valid JSON object.';
                return null;
            }
        }
        return {
            companyId,
            sqlText: this.sqlText,
            parameters,
            maxRows: Math.max(1, Math.min(Number(this.sqlMaxRows) || 100, 500))
        };
    }

    private async initSqlEditor(): Promise<void> {
        if (this.sqlEditorInitializing || this.sqlEditor || !this.sqlEditorHost?.nativeElement) {
            this.sqlEditor?.layout?.();
            return;
        }
        this.sqlEditorInitializing = true;
        try {
            const monaco = await this.loadMonaco();
            this.sqlEditor = monaco.editor.create(this.sqlEditorHost.nativeElement, {
                value: this.sqlText,
                language: 'sql',
                theme: 'vs',
                automaticLayout: true,
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                wordWrap: 'on',
                readOnly: !this.canEditSelectedSql(),
                scrollBeyondLastLine: false
            });
            this.sqlEditor.onDidChangeModelContent(() => {
                this.sqlText = this.sqlEditor.getValue();
                this.sqlValidation = undefined;
            });
        } catch {
            this.sqlEditorFallback = true;
        } finally {
            this.sqlEditorInitializing = false;
        }
    }

    private setSqlEditorValue(value: string): void {
        this.sqlText = value;
        if (this.sqlEditor) {
            this.sqlEditor.setValue(value);
            this.sqlEditor.updateOptions({ readOnly: !this.canEditSelectedSql() });
        }
        setTimeout(() => this.initSqlEditor());
    }

    private apiError(error: any, fallback: string): string {
        if (typeof error?.error === 'string' && error.error.trim()) {
            return error.error;
        }
        if (typeof error?.error?.message === 'string') {
            return error.error.message;
        }
        return fallback;
    }

    private defaultSqlText(): string {
        return `select
    shipment_number,
    shipment_status,
    pickup_city,
    dropoff_city,
    created_at
from shipments
where created_at >= :fromDate
order by created_at desc`;
    }

    private defaultTemplateHtml(): string {
        return `<!doctype html>
<html lang="en" xmlns:th="http://www.thymeleaf.org">
<head>
  <meta charset="utf-8"/>
  <title th:text="\${reportType}">Report Template</title>
  <style>
    body { font-family: Arial, sans-serif; color: #172033; margin: 24px; }
    h1 { color: #1f5f74; }
  </style>
</head>
<body>
  <h1 th:text="\${reportType}">Report Template</h1>
  <p>Use the binding catalog to insert approved Thymeleaf expressions.</p>
</body>
</html>`;
    }

    private titleCase(value: string): string {
        return (value || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    }

    private slug(value: string): string {
        return (value || 'template').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    }
}
